import {
  OperationVariables,
  QueryHookOptions,
  QueryResult,
} from '@apollo/client';
import { compact } from 'lodash';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useFormContext } from '@/components/react-hook-form';
import { DisplayTable } from '@/components/tables/DisplayTable/DisplayTable';
import { TableRowSkeleton } from '@/components/tables/TableRowSkeleton/TableRowSkeleton';
import { useReportError } from '@/hooks/useReportError';
import { AugmentedCreateEntityInput } from '@/types/schema';
import { PathsOf } from '@/types/subform';

import { getKindFromProvider } from '../assetProviderIntegrations/assetProviderIntegrations.utils';
import { useDefaultAssetClassId } from '../tenant/TenantDetailsContext/hooks/useDefaultAssetClassId';
import { useTenantDetailsContext } from '../tenant/TenantDetailsContext/TenantDetailsContext';
import { mapAddeparEntityQueryResponseToTable } from './addepar/EntityImportTable.addepar';
import {
  BulkImportFlowType,
  useBulkImportFlowContext,
} from './BulkImportFlowContext';
import {
  EntityImportTableClientRow,
  EntityImportTableEmptyRows,
} from './EntityImportTable.components';
import {
  EntityImportHouseholdMap,
  EntityImportTableForm,
  NAMESPACE,
} from './EntityImportTable.types';
import { mapFormDataToMutation } from './EntityImportTable.utils';
import {
  AllHouseholdsEntityImportTableQueryVariables,
  useAllHouseholdsEntityImportTableQuery,
  useSingleHouseholdEntityImportTableQuery,
} from './graphql/EntityImportTable.generated';
import { useEntityTableImportMutation } from './graphql/EntityTableImport.generated';

/**
 * Header text for the two columns whose wording varies by flow. The defaults
 * suit the multi-household bulk import; single-household flows that don't show
 * a client grouping row override them.
 */
export interface EntityImportTableColumnLabels {
  unlinkedEntities?: string;
  displayName?: string;
}

const DEFAULT_COLUMN_LABELS: Required<EntityImportTableColumnLabels> = {
  unlinkedEntities: 'Clients + unlinked entities',
  displayName: 'Display name',
};

export interface EntityImportTableProps {
  initialExpandedHouseholdIDs?: string[];
  tableCaption?: JSX.Element;
  singleHouseholdId?: string;
  columnLabels?: EntityImportTableColumnLabels;
  emptyState?: {
    heading: string;
    description: string;
  };
  onAvailabilityChange?: (hasAvailableEntities: boolean) => void;
  /**
   * Give the import checkbox its own leading column instead of nesting it
   * beside the entity name. Off by default; the Manage integration data flow
   * turns it on so this table matches the liabilities and life-insurance
   * tables it sits alongside.
   */
  separateCheckboxColumn?: boolean;
}

export function EntityImportTable(props: EntityImportTableProps) {
  return (
    <EntityImportTableEmbeddableInner
      {...props}
      fetchData={
        props.singleHouseholdId
          ? useSingleHouseholdEntityImportTableQuery
          : useAllHouseholdsEntityImportTableQuery
      }
      mapDataToTable={mapAddeparEntityQueryResponseToTable}
    />
  );
}

type EntityLookupVariables = AllHouseholdsEntityImportTableQueryVariables &
  OperationVariables;

interface EntityImportTableInnerProps<TData> extends EntityImportTableProps {
  fetchData: (
    options: QueryHookOptions<TData, EntityLookupVariables>
  ) => QueryResult<TData, EntityLookupVariables>;
  mapDataToTable: (queryResponse: TData) => EntityImportTableForm;
  singleHouseholdId?: string;
}

function getVariables(
  singleHouseholdId: string | undefined,
  flowType: BulkImportFlowType
): EntityLookupVariables {
  const integrationClientKind = getKindFromProvider(flowType);

  if (singleHouseholdId) {
    // for this case, if a household not connected with addepar is selected, they'll see the
    // "No entities availble to import" case, as the query should still check that the entities
    // available for import are associated with of the (missing) addepar integration entities
    return {
      integrationClientKind,
      householdsLike: { id: singleHouseholdId },
    };
  } else {
    return {
      integrationClientKind,
      householdsLike: {
        hasIntegrationClientsWith: [
          {
            kind: integrationClientKind,
          },
        ],
      },
    };
  }
}

function EntityImportTableEmbeddableInner<TData>({
  initialExpandedHouseholdIDs = [],
  fetchData,
  mapDataToTable,
  tableCaption,
  singleHouseholdId,
  columnLabels,
  emptyState,
  onAvailabilityChange,
  separateCheckboxColumn = false,
}: EntityImportTableInnerProps<TData>) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { flowType } = useBulkImportFlowContext();
  const { reset, control, getValues } = useFormContext<EntityImportTableForm>();
  const isSingleHouseholdMode = !!singleHouseholdId;

  const variables = getVariables(singleHouseholdId, flowType);
  const { loading } = fetchData({
    variables,
    // always pull latest from network, since the local cache
    // may have been made stale by potential earlier steps in the flow
    fetchPolicy: 'network-only',
    onError: (error) => {
      showFeedback(
        'Could not load entities for import.  Please refresh the page and try again.'
      );
      reportError(
        'Caught error while loading households and entities for import',
        error
      );
    },
    onCompleted: (data) => {
      const tableData = mapDataToTable(data);
      reset({ ...getValues(), ...tableData });
      onAvailabilityChange?.(
        Object.keys(tableData[NAMESPACE].householdMap).length > 0
      );
    },
  });

  const householdMap = useWatch<EntityImportTableForm>({
    control,
    name: `${NAMESPACE}.householdMap` as const satisfies PathsOf<EntityImportTableForm>,
  }) as EntityImportHouseholdMap;

  const householdIds = useMemo<string[]>(
    () => Object.keys(householdMap),
    [householdMap]
  );

  // Five content columns, plus the checkbox column when it's split out.
  const skeletonColumnCount = separateCheckboxColumn ? 6 : 5;

  if (!loading && !householdIds.length) {
    return <EntityImportTableEmptyRows {...emptyState} />;
  }

  return (
    <>
      {tableCaption}
      <DisplayTable
        columns={compact([
          separateCheckboxColumn && { headerName: '', width: 40 },
          {
            headerName:
              columnLabels?.unlinkedEntities ??
              DEFAULT_COLUMN_LABELS.unlinkedEntities,
            flex: 1,
          },
          { headerName: 'Value', width: 150, align: 'right' },
          {
            headerName:
              columnLabels?.displayName ?? DEFAULT_COLUMN_LABELS.displayName,
            flex: 1,
          },
          {
            headerName: 'Entity type',
            width: 225,
          },
          {
            headerName: 'Grantors, owners or donors',
            flex: 1,
          },
        ])}
      >
        {loading ? (
          <>
            <TableRowSkeleton columns={skeletonColumnCount} />
            <TableRowSkeleton columns={skeletonColumnCount} />
            <TableRowSkeleton columns={skeletonColumnCount} />
            <TableRowSkeleton columns={skeletonColumnCount} />
          </>
        ) : (
          compact(
            householdIds
              .filter((householdId) => {
                if (
                  // single house hold mode & matching -- display household
                  isSingleHouseholdMode &&
                  singleHouseholdId === householdId
                ) {
                  return true;
                } else if (!isSingleHouseholdMode) {
                  // not single household mode -- display household
                  return true;
                }
                // single household mode and not matching -- do not display
                return false;
              })
              .map((householdId) => (
                <EntityImportTableClientRow
                  key={householdId}
                  householdId={householdId}
                  isInitiallyExpanded={initialExpandedHouseholdIDs.includes(
                    householdId
                  )}
                  isSingleHouseholdMode={isSingleHouseholdMode}
                  separateCheckboxColumn={separateCheckboxColumn}
                />
              ))
          )
        )}
      </DisplayTable>
    </>
  );
}

export interface EntityImportTableSubmitButtonProps {
  onSuccessfulSubmit: () => void;
  text: string;
}

function EntityImportTableSubmitButtonInner({
  onSuccessfulSubmit,
  text,
}: EntityImportTableSubmitButtonProps) {
  const { assetClassesById } = useTenantDetailsContext();
  const defaultAssetClassId = useDefaultAssetClassId();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const {
    formState: { isSubmitting: formIsSubmitting },
    handleSubmit,
  } = useFormContext<EntityImportTableForm>();

  const [importEntities, { loading: importEntitiesLoading }] =
    useEntityTableImportMutation({
      onCompleted: () => {
        showFeedback(
          'Selected entities were successfully linked into Luminary',
          { variant: 'success' }
        );
      },
      onError: (err) => {
        reportError('error importing entities', err);
        showFeedback(
          'There was an error importing entities.  Please try again.'
        );
      },
    });

  const onSubmit = handleSubmit((formData: EntityImportTableForm) => {
    const inputs: AugmentedCreateEntityInput[] = mapFormDataToMutation(
      formData,
      {
        defaultAssetClassId,
        assetClassesById,
      }
    );

    if (!inputs.length) {
      showFeedback('Please select at least one entity to import', {
        variant: 'info-high',
      });
      return;
    }

    return importEntities({
      variables: {
        inputs,
      },
      onCompleted: () => {
        showFeedback(
          'Selected entities were successfully linked into Luminary',
          { variant: 'success' }
        );
        onSuccessfulSubmit();
      },
    });
  });

  return (
    <FormFieldsDisabledProvider
      isDisabled={formIsSubmitting || importEntitiesLoading}
    >
      <Button
        variant="primary"
        size="sm"
        onClick={onSubmit}
        loading={formIsSubmitting || importEntitiesLoading}
      >
        {text}
      </Button>
    </FormFieldsDisabledProvider>
  );
}

export function EntityImportTableSubmitButton(
  props: EntityImportTableSubmitButtonProps
) {
  return <EntityImportTableSubmitButtonInner {...props} />;
}
